<template>
  <div class="AiAccommodationDetailsPrice" role="rowgroup">
    <!-- No member offer -->
    <template v-if="!memberOffer">
      <div class="AiAccommodationDetailsPrice-priceContainer" role="row">
        <ai-amount
          role="cell"
          :amount="convertedPublicPrice.amount"
          :symbol="convertedPublicPrice.currency?.code" />
      </div>
    </template>

    <!-- Member offer - Not logged -->
    <template v-if="memberOffer && !isLogged">
      <div class="AiAccommodationDetailsPrice-priceContainer" role="row">
        <ai-typo
          role="cell"
          variant="overline-01"
          as="span"
          class="AiAccommodationDetailsPrice-priceLabel">
          {{ $t('booking.rooms.prices.public.label') }}
        </ai-typo>
        <ai-amount
          role="cell"
          :amount="convertedPublicPrice.amount"
          :symbol="convertedPublicPrice.currency?.code" />
      </div>

      <div
        role="row"
        class="AiAccommodationDetailsPrice-priceContainer AiAccommodationDetailsPrice-priceContainer--background">
        <ai-typo
          role="cell"
          variant="overline-01"
          as="span"
          class="AiAccommodationDetailsPrice-priceLabel">
          {{ $t('booking.rooms.prices.member.label') }}
        </ai-typo>
        <ai-amount
          role="cell"
          :amount="convertedMemberPrice.amount"
          :symbol="convertedMemberPrice.currency?.code" />
      </div>
    </template>

    <!-- Member offer - Logged -->
    <template v-if="memberOffer && isLogged">
      <div class="AiAccommodationDetailsPrice-priceContainer" role="row">
        <ai-amount
          role="cell"
          :amount="convertedMemberPrice.amount"
          :symbol="convertedMemberPrice.currency?.code"
          member-tag="right"
          member-tag-top-offset="0" />
      </div>

      <div
        class="AiAccommodationDetailsPrice-priceContainer AiAccommodationDetailsPrice-striked"
        role="row">
        <ai-amount
          role="cell"
          :amount="convertedPublicPrice.amount"
          :symbol="convertedPublicPrice.currency?.code"
          main-typo="paragraph-03-regular"
          decimal-typo="paragraph-03-regular" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { isPreferredOffer } from '~/domains/booking/helpers/isOffer.helper';

import { useAvailabilityRoomCheapestOffer } from '~~/domains/booking';
import type { Accommodation, AvailabilityRoom } from '~~/domains/graphql';
import { getOfferPrice } from '~~/helpers';

type Props = {
  accommodation?: Accommodation | null;
  availabilityRoom: AvailabilityRoom;
  userCurrency?: string;
  allTaxesIncluded?: boolean;
  isLogged: boolean;
};

const props = defineProps<Props>();

const { cheapestOffer, isCheapestOfferAMemberOffer } =
  useAvailabilityRoomCheapestOffer(props.availabilityRoom);

const publicOffer = computed(() => {
  if (!isCheapestOfferAMemberOffer.value) return cheapestOffer.value;

  const offers = props.availabilityRoom.offers ?? [];
  const isPreferredOfferExists = offers?.some(offer =>
    offer?.categories?.some(isPreferredOffer),
  );

  const linkedPublicOfferCode = (cheapestOffer.value?.code ?? '').split('-')[0];

  const linkedPublicOffer = offers?.find(
    offer => offer?.code === linkedPublicOfferCode,
  );

  // This is in case the user has applied a corporate code
  // We want to check if there is a preferred offer with a lower price than the public offer
  // And display the lower price as a "*From rate" in the booking/room page
  if (isPreferredOfferExists) {
    const preferredOfferWithLowerPriceThanPublicOffer = offers?.find(
      offer =>
        offer?.categories?.some(isPreferredOffer) &&
        (offer?.pricing?.amount?.hotelKeeper ?? 0) <
          (linkedPublicOffer?.pricing?.amount?.hotelKeeper ?? 0),
    );

    if (preferredOfferWithLowerPriceThanPublicOffer) {
      return preferredOfferWithLowerPriceThanPublicOffer;
    }
  }

  return linkedPublicOffer;
});

const memberOffer = computed(() => {
  if (isCheapestOfferAMemberOffer.value) return cheapestOffer.value;
  return null;
});

const hotelCurrency = computed(() => {
  return publicOffer.value?.pricing?.currency as string | undefined;
});

const publicPrice = computed(() => {
  if (!publicOffer.value) return 0;

  return getOfferPrice(props.allTaxesIncluded, publicOffer.value);
});

const memberPrice = computed(() => {
  if (!memberOffer.value) return 0;

  return getOfferPrice(props.allTaxesIncluded, memberOffer.value);
});

const userCurrency = computed(() => props.userCurrency);

const convertedPublicPrice = useConvertToUserCurrency(
  publicPrice,
  hotelCurrency,
  userCurrency,
);

const convertedMemberPrice = useConvertToUserCurrency(
  memberPrice,
  hotelCurrency,
  userCurrency,
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';

.AiAccommodationDetailsPrice-priceContainer {
  position: relative;
  width: max-content;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: constants.$inner-02;
  padding-right: constants.$inner-02;

  &--background {
    background-color: colors.$gold-100;
  }
}

.AiAccommodationDetailsPrice-priceLabel {
  display: inline-block;
  text-transform: uppercase;
  margin-right: constants.$margin-01;
}

.AiAccommodationDetailsPrice-striked {
  text-decoration: line-through;
}
</style>
